import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import AuxcapLogo from '../../assets/images/AuxcapLogo.svg';
import { RootState } from '../../store';

interface BrandLogoProps {
    alt?: string;
    style?: React.CSSProperties;
    className?: string;
    onClick?: () => void;
}

/**
 * BrandLogo renders the portal's custom logo from the brand API, or falls back to
 * the bundled Auxcap logo if the portal has no custom logo configured.
 *
 * App.tsx blocks rendering until brand is loaded, so isBrandLoading is always false
 * by the time this component mounts. The brandDetails null guard handles API errors.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({ alt = 'Logo', style, className, onClick }) => {
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const logoPath = brandDetails?.Logo;
    const [logoError, setLogoError] = useState(false);
    const [prevLogoPath, setPrevLogoPath] = useState(logoPath);

    // Reset error state when the logo path changes (brand data refreshed). Adjusting state during
    // render is React's recommended alternative to a setState-in-effect for prop-derived resets.
    if (logoPath !== prevLogoPath) {
        setPrevLogoPath(logoPath);
        setLogoError(false);
    }

    // A clickable logo needs to be a real interactive element rather than an <img> with
    // bolted-on click/keyboard handlers, so wrap it in a <button> when onClick is provided.
    // When there is no onClick the logo stays a plain, non-interactive image.
    const renderLogo = (src: string, onError?: () => void) => {
        const img = <img src={src} alt={alt} style={style} className={className} onError={onError} />;
        if (!onClick) return img;

        return (
            <button
                type="button"
                onClick={onClick}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
                {img}
            </button>
        );
    };

    // Brand API failed (null brandDetails after load) — fall back to default
    if (!brandDetails) {
        return renderLogo(AuxcapLogo);
    }

    // Construct the custom logo URL if the portal has one configured
    let customLogoSrc: string | null = null;
    if (logoPath && logoPath.trim() !== '') {
        if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
            customLogoSrc = logoPath;
        } else {
            const profileUrlBase = process.env.REACT_APP_PROFILE_URL || '';
            customLogoSrc = `${profileUrlBase}${logoPath}`;
        }
    }

    // No custom logo configured for this portal, or it failed to load → Auxcap default
    if (!customLogoSrc || logoError) {
        return renderLogo(AuxcapLogo);
    }

    // Render the portal's custom logo
    return renderLogo(customLogoSrc, () => setLogoError(true));
};
